import React from "react";

/**
 * Bidorah visual identity.
 *
 *  - Mark: rounded-square monogram with a sharpened "B", an amber
 *    ascending-bid tick across the top-right corner, and a live-pulse dot.
 *    The tick reads as a bid climbing; the dot says "live."
 *  - Wordmark: Cabinet Grotesk Black at -0.055em with a subtle underline
 *    accent beneath the "i" to echo the live-dot on the mark.
 *
 * Animation (when `withPulse`):
 *  - Amber dot heartbeats (scale + opacity) on a 1.8s loop.
 *  - A soft amber halo ring expands and fades behind the dot on the same beat.
 *  - The linear gradient on the card subtly breathes on a 6s loop.
 *  - The small dot beside "Bidorah" in the wordmark heartbeats in lock-step.
 *
 * All motion respects prefers-reduced-motion: reduce.
 *
 * Usage:
 *   <BidoraLogo />                  // mark + wordmark, default
 *   <BidoraLogo size={56} />        // bigger
 *   <BidoraLogo variant="mark" />   // mark only (favicons, avatars)
 *   <BidoraLogo inverse />          // on dark surfaces
 */
export default function BidoraLogo({
  size = 36,
  variant = "full", // "full" | "mark" | "wordmark"
  inverse = false,
  withPulse = true,
  className = "",
}) {
  const fg = inverse ? "#FFFFFF" : "#0A1929";
  const accent = "#1D78AB";
  const markOnly = variant === "mark";
  const wordOnly = variant === "wordmark";
  const wordSize = Math.round(size * 0.62);

  const reducedMotion = useReducedMotion();
  const animate = withPulse && !reducedMotion;

  return (
    <span
      className={`inline-flex items-center gap-2.5 select-none ${className}`}
      aria-label="Bidorah"
    >
      {!wordOnly && <BidoraMark size={size} animate={animate} />}
      {!markOnly && (
        <span
          className="font-display font-black leading-none"
          style={{
            fontSize: wordSize,
            letterSpacing: "-0.055em",
            color: fg,
            fontWeight: 900,
          }}
        >
          Bidorah
          <span
            aria-hidden="true"
            className={animate ? "bidora-wordmark-dot" : ""}
            style={{
              display: "inline-block",
              width: Math.max(3, Math.round(size * 0.08)),
              height: Math.max(3, Math.round(size * 0.08)),
              borderRadius: "9999px",
              background: accent,
              marginLeft: Math.round(size * 0.04),
              verticalAlign: "baseline",
              // expose translateY so the keyframe can preserve the existing baseline offset
              ["--bidora-dot-ty"]: `-${Math.round(wordSize * 0.05)}px`,
              transform: `translateY(-${Math.round(wordSize * 0.05)}px)`,
            }}
          />
        </span>
      )}
    </span>
  );
}

/* ---------------- prefers-reduced-motion hook ---------------- */
function useReducedMotion() {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReduced(mq.matches);
    handler();
    // addEventListener is preferred; fall back to addListener for older Safari.
    if (mq.addEventListener) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);
  return reduced;
}

/* ---------------- Mark ---------------- */
function BidoraMark({ size = 36, animate = true }) {
  // Unique IDs per instance so multiple marks on the same page don't share
  // gradient state (and their SMIL animations don't collide).
  const reactId = React.useId();
  const uid = reactId.replace(/:/g, "");
  const bgId = `bidora-bg-${uid}`;
  const shineId = `bidora-shine-${uid}`;

  return (
    <span
      className="relative inline-block"
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 40 40"
        width={size}
        height={size}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={bgId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2190CC">
              {animate && (
                <animate
                  attributeName="stop-color"
                  values="#2190CC;#29A2DB;#2190CC"
                  dur="6s"
                  repeatCount="indefinite"
                />
              )}
            </stop>
            <stop offset="55%" stopColor="#1D78AB">
              {animate && (
                <animate
                  attributeName="stop-color"
                  values="#1D78AB;#2287BF;#1D78AB"
                  dur="6s"
                  repeatCount="indefinite"
                />
              )}
            </stop>
            <stop offset="100%" stopColor="#0E3F5C" />
          </linearGradient>
          <linearGradient id={shineId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.28" />
            <stop offset="55%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Card */}
        <rect x="0" y="0" width="40" height="40" rx="10" fill={`url(#${bgId})`} />
        {/* Top-left shine */}
        <rect x="0" y="0" width="40" height="22" rx="10" fill={`url(#${shineId})`} />

        {/* Didone "B" — thick stem with hairline bowls and bracketed serifs */}
        <rect x="10.5" y="8.5" width="3.8" height="23" fill="#FFFFFF" />
        <path
          d="M14.3 8.5 H20.5 C23.6 8.5 26.2 10.6 26.2 13.7 C26.2 16.6 24.2 18.6 20.5 18.7 H14.3"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="2.5"
        />
        <path
          d="M14.3 18.9 H21.1 C24.6 18.9 27.3 21.3 27.3 24.9 C27.3 28.5 24.9 31.5 20.5 31.5 H14.3"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="2.5"
        />
        {/* Bracketed stem serifs */}
        <rect x="9" y="8.5" width="6.8" height="1.1" fill="#FFFFFF" />
        <rect x="9" y="30.4" width="6.8" height="1.1" fill="#FFFFFF" />

        {/* Amber halo — expands + fades behind the dot (shockwave) */}
        <circle cx="30.5" cy="31.5" r="1.6" fill="#F59E0B" opacity="0.35">
          {animate && (
            <>
              <animate
                attributeName="r"
                values="1.6;4.4;1.6"
                dur="1.8s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.45;0;0.45"
                dur="1.8s"
                repeatCount="indefinite"
              />
            </>
          )}
        </circle>

        {/* Amber quote accent — heartbeats (scale + opacity) */}
        <circle cx="30.5" cy="31.5" r="1.6" fill="#F59E0B">
          {animate && (
            <>
              <animate
                attributeName="r"
                values="1.6;2.0;1.6"
                dur="1.8s"
                calcMode="spline"
                keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
                keyTimes="0; 0.5; 1"
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="1;0.7;1"
                dur="1.8s"
                repeatCount="indefinite"
              />
            </>
          )}
        </circle>
      </svg>
    </span>
  );
}

export { BidoraMark };
